const isBrowser = typeof window !== "undefined"
const CRISP_CHAT_OWNER_KEY = "mp_crisp_chat_account"
const CRISP_VERIFIED_STORAGE_KEY = "mp_crisp_identity_verified"
const CRISP_EMAIL_GUARD_KEY = "__mountProxiesCrispVerifiedEmail"
const CRISP_EMAIL_GUARD_INSTALLED_KEY = "__mountProxiesCrispEmailGuardInstalled"

const getCrispQueue = () => {
  if (!isBrowser) return null

  window.$crisp = window.$crisp || []
  return window.$crisp
}

const isSafeCrispValue = value =>
  ["string", "boolean", "number"].includes(typeof value)

const setSessionData = data => {
  const crisp = getCrispQueue()
  if (!crisp) return

  const pairs = Object.keys(data)
    .filter(key => isSafeCrispValue(data[key]) && data[key] !== "")
    .map(key => [key, data[key]])

  if (pairs.length) {
    crisp.push(["set", "session:data", [pairs]])
  }
}

const wasCrispIdentityVerified = () => {
  if (!isBrowser) return false

  try {
    return window.localStorage.getItem(CRISP_VERIFIED_STORAGE_KEY) === "true"
  } catch (e) {
    return false
  }
}

const setCrispIdentityVerifiedFlag = verified => {
  if (!isBrowser) return

  try {
    if (verified) {
      window.localStorage.setItem(CRISP_VERIFIED_STORAGE_KEY, "true")
    } else {
      window.localStorage.removeItem(CRISP_VERIFIED_STORAGE_KEY)
    }
  } catch (e) {
    return
  }
}

const setVerifiedEmailGuardValue = email => {
  if (!isBrowser) return

  window[CRISP_EMAIL_GUARD_KEY] = String(email || "")
    .trim()
    .toLowerCase()
}

const installCrispEmailGuard = crisp => {
  if (!isBrowser || window[CRISP_EMAIL_GUARD_INSTALLED_KEY]) return

  window[CRISP_EMAIL_GUARD_INSTALLED_KEY] = true
  crisp.push([
    "on",
    "user:email:changed",
    email => {
      const verifiedEmail = window[CRISP_EMAIL_GUARD_KEY]
      const changedEmail = String(email || "")
        .trim()
        .toLowerCase()

      if (changedEmail && (!verifiedEmail || changedEmail !== verifiedEmail)) {
        if (verifiedEmail) markCrispIdentityUnverified({ resetSession: true })
        else disconnectCrispAccountContext("unavailable")
      }
    },
  ])
}

// Revoked account authority does not revoke an operator conversation. Clear
// account-derived metadata without resetting the SDK session or its messages.
export const disconnectCrispAccountContext = (status = "sign_in_required") => {
  const crisp = getCrispQueue()
  if (!crisp) return
  // Retain upgrade-era ownership even if older code stored only the flag.
  try {
    if (wasCrispIdentityVerified() || window[CRISP_EMAIL_GUARD_KEY]) {
      if (!window.localStorage.getItem(CRISP_CHAT_OWNER_KEY)) window.localStorage.setItem(CRISP_CHAT_OWNER_KEY, window[CRISP_EMAIL_GUARD_KEY] || "previous-account")
    }
  } catch (e) {}
  setCrispIdentityVerifiedFlag(false)
  setVerifiedEmailGuardValue("")
  installCrispEmailGuard(crisp)
  setSessionData({
    identity_verified: false,
    account_email: "-",
    account_id: "-",
    visitor_scope: "public_visitor",
    support_context_status: status,
    plan_name: "NA",
    subscription_status: "NA",
    account_created_at: "-",
  })
}

export const markCrispIdentityUnverified = ({
  resetSession = false,
  reloadPage = false,
  accountEmail = "",
  accountId = "",
  metadata = {},
} = {}) => {
  const crisp = getCrispQueue()
  if (!crisp) return

  let hadAccountConversation = false
  try { hadAccountConversation = !!window.localStorage.getItem(CRISP_CHAT_OWNER_KEY) } catch (e) {}
  if (resetSession || wasCrispIdentityVerified() || hadAccountConversation) {
    if (reloadPage) {
      crisp.push(["do", "session:reset", [true]])
    } else {
      crisp.push(["do", "session:reset"])
    }
  }

  try { window.localStorage.removeItem(CRISP_CHAT_OWNER_KEY) } catch (e) {}
  setCrispIdentityVerifiedFlag(false)
  setVerifiedEmailGuardValue("")
  installCrispEmailGuard(crisp)

  setSessionData({
    identity_verified: false,
    account_email: accountEmail || "-",
    account_id: accountId || metadata.account_id || "-",
    visitor_scope: "public_visitor",
    plan_name: metadata.plan_name || "NA",
    subscription_status: metadata.subscription_status || "NA",
    account_created_at: metadata.account_created_at || "-",
  })
}

export const applyCrispVerifiedIdentity = verification => {
  const crisp = getCrispQueue()
  if (!crisp) return

  const email = verification && verification.email
  const hmac = verification && verification.hmac

  if (!email || !hmac) {
    markCrispIdentityUnverified({
      accountEmail: verification && verification.account_email,
      accountId: verification && verification.account_id,
      metadata: verification && verification.metadata,
    })
    return
  }

  const metadata = verification.metadata || {}
  try {
    const owner = window.localStorage.getItem(CRISP_CHAT_OWNER_KEY)
    const nextOwner = verification.account_id || metadata.account_id || email
    if (owner && owner !== nextOwner && owner !== email) markCrispIdentityUnverified({ resetSession: true })
    window.localStorage.setItem(CRISP_CHAT_OWNER_KEY, nextOwner)
  } catch (e) {}

  crisp.push(["set", "user:email", [email, hmac]])
  setCrispIdentityVerifiedFlag(true)
  setVerifiedEmailGuardValue(email)
  installCrispEmailGuard(crisp)

  setSessionData({
    identity_verified: true,
    account_email: verification.account_email || email,
    account_id: verification.account_id || metadata.account_id || "",
    visitor_scope: "authenticated_customer",
    support_context_status: "connected",
    plan_name: metadata.plan_name || "",
    subscription_status: metadata.subscription_status || "",
    account_created_at: metadata.account_created_at || "",
  })
}
